import { type MqttAreaUpdateMessage } from '@/context/messaging/ekanban';
import { type SubscriptionTuple } from '@/context/messaging/general';
import { MqttEvent, MqttScope, MqttTopicVersion } from '@/context/messaging/mqtt';
import { type MqttOrderUpdate } from '@/context/messaging/picking';
import { type DefaultEventsMap } from '@socket.io/component-emitter';
import { z } from 'zod';

export enum MessageEvent {
  ECHO_REQUEST = 'echo-request',
  ECHO_RESPONSE = 'echo-response',
  MQTT_SUBSCRIBE = 'mqtt-subscribe',
  /** Heartbeat lease renewal: re-registers a subscription server-side, only acknowledged if the subscription was lost */
  MQTT_SUBSCRIBE_RENEW = 'mqtt-subscribe-renew',
  MQTT_SUBSCRIPTION_ACKNOWLEDGED = 'mqtt-subscription-acknowledged',
  MQTT_SUBSCRIPTION_CANCELED = 'mqtt-subscription-canceled',
  MQTT_UNSUBSCRIBE = 'mqtt-unsubscribe',
  ORDER_UPDATE = 'order-update',
  UPDATE = 'update',
  CLIENT_CONNECTED = 'client-connected',
  CLIENT_DISCONNECTED = 'client-disconnected',
}

export interface ClientToServerMessages {
  [MessageEvent.ECHO_REQUEST]: string;
  [MessageEvent.MQTT_SUBSCRIBE]: SubscriptionPayload;
  [MessageEvent.MQTT_SUBSCRIBE_RENEW]: SubscriptionPayload;
  [MessageEvent.MQTT_UNSUBSCRIBE]: SubscriptionPayload;
  [MessageEvent.CLIENT_CONNECTED]: any;
  [MessageEvent.CLIENT_DISCONNECTED]: any;
}

export interface ServerToClientMessages {
  [MessageEvent.ECHO_RESPONSE]: string;
  [MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED]: SubscriptionPayload;
  [MessageEvent.MQTT_SUBSCRIPTION_CANCELED]: SubscriptionPayload;
  [MessageEvent.ORDER_UPDATE]: any; // @FIXME: Create proper type, which is identical to what the backend sends out.
  [MessageEvent.UPDATE]: any; // @FIXME: Create proper type, which is identical to what the backend sends out.
}

export interface SocketIOEventsMap extends DefaultEventsMap {
  [MessageEvent.UPDATE]: (message: MqttAreaUpdateMessage, sub?: SubscriptionTuple) => void;
  [MessageEvent.ORDER_UPDATE]: (message: MqttOrderUpdate, sub?: SubscriptionTuple) => void;
  [MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED]: (message: SubscriptionPayload) => void;
  [MessageEvent.MQTT_SUBSCRIPTION_CANCELED]: (message: SubscriptionPayload) => void;
}

//
// Client to Server
//

const mqttTopicVersionSchema = z.enum(MqttTopicVersion);

const baseSubscriptionSchema = z.object({
  version: mqttTopicVersionSchema,
  topic: z.string(),
});

const pickingOrderUpdateSubscriptionSchema = baseSubscriptionSchema.extend({
  scope: z.literal(MqttScope.PICKING_PICKER_PROFILES),
  type: z.literal(MqttEvent.ORDER_UPDATE),
});

const ekanbanReceivingAreaUpdateSubscriptionSchema = baseSubscriptionSchema.extend({
  scope: z.literal(MqttScope.RECEIVING_AREAS),
  type: z.literal(MqttEvent.UPDATE),
});

const subscriptionSchema = z.discriminatedUnion('scope', [
  pickingOrderUpdateSubscriptionSchema,
  ekanbanReceivingAreaUpdateSubscriptionSchema,
]);

const baseClientToServerSchema = z.object({
  sender: z.string(),
  args: z.array(z.any()),
});

const subscribeSchema = baseClientToServerSchema.extend({
  event: z.literal(MessageEvent.MQTT_SUBSCRIBE),
  args: z.tuple([subscriptionSchema]).rest(z.any()),
});

const subscribeRenewSchema = baseClientToServerSchema.extend({
  event: z.literal(MessageEvent.MQTT_SUBSCRIBE_RENEW),
  args: z.tuple([subscriptionSchema]).rest(z.any()),
});

const unsubscribeSchema = baseClientToServerSchema.extend({
  event: z.literal(MessageEvent.MQTT_UNSUBSCRIBE),
  args: z.tuple([subscriptionSchema]).rest(z.any()),
});

const echoRequestSchema = baseClientToServerSchema.extend({
  event: z.literal(MessageEvent.ECHO_REQUEST),
  args: z.tuple([z.string()]).rest(z.any()),
});

const clientConnectedSchema = baseClientToServerSchema.extend({
  event: z.literal(MessageEvent.CLIENT_CONNECTED),
  // args: z.tuple([z.any()]).rest(z.any()),
});

const clientDisconnectedSchema = baseClientToServerSchema.extend({
  event: z.literal(MessageEvent.CLIENT_DISCONNECTED),
  // args: z.tuple([z.undefined()]).rest(z.any()),
});

export const clientToServerMessageEnvelopeSchema = z.discriminatedUnion('event', [
  subscribeSchema,
  subscribeRenewSchema,
  unsubscribeSchema,
  echoRequestSchema,
  clientConnectedSchema,
  clientDisconnectedSchema,
]);

export type ClientToServerMessageEnvelope = z.infer<typeof clientToServerMessageEnvelopeSchema>;
export type SubscriptionPayload = z.infer<typeof subscriptionSchema>;

export function assertClientToServerMessageEnvelope(
  candidate: unknown,
): asserts candidate is ClientToServerMessageEnvelope {
  clientToServerMessageEnvelopeSchema.parse(candidate);
}
export function isClientToServerMessageEnvelope(candidate: unknown): candidate is ClientToServerMessageEnvelope {
  return clientToServerMessageEnvelopeSchema.safeParse(candidate).success;
}

export function isSubscriptionPayload(candidate: unknown): candidate is SubscriptionPayload {
  return subscriptionSchema.safeParse(candidate).success;
}

//
// Server to client
//

const baseServerToClientSchema = z.object({
  sender: z.string(),
  args: z.array(z.any()),
});

const echoResponseSchema = baseServerToClientSchema.extend({
  event: z.literal(MessageEvent.ECHO_RESPONSE),
  args: z.tuple([z.string()]).rest(z.any()),
});

const orderUpdateSchema = baseServerToClientSchema.extend({
  event: z.literal(MessageEvent.ORDER_UPDATE),
  // @TODO: build proper type checking for this based on the real object provided by the backend
});

const updateSchema = baseServerToClientSchema.extend({
  event: z.literal(MessageEvent.UPDATE),
});

const mqttSubscriptionAcknowledged = baseServerToClientSchema.extend({
  event: z.literal(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED),
  args: z.tuple([subscriptionSchema]).rest(z.any()),
});

const mqttSubscriptionCanceled = baseServerToClientSchema.extend({
  event: z.literal(MessageEvent.MQTT_SUBSCRIPTION_CANCELED),
  args: z.tuple([subscriptionSchema]).rest(z.any()),
});

export const serverToClientMessageEnvelopeSchema = z.discriminatedUnion('event', [
  echoResponseSchema,
  orderUpdateSchema,
  updateSchema,
  mqttSubscriptionAcknowledged,
  mqttSubscriptionCanceled,
]);

export type ServerToClientMessageEnvelope = z.infer<typeof serverToClientMessageEnvelopeSchema>;

export function assertServerToClientMessageEnvelope(
  candidate: unknown,
): asserts candidate is ServerToClientMessageEnvelope {
  serverToClientMessageEnvelopeSchema.parse(candidate);
}
export function isServerToClientMessageEnvelope(candidate: unknown): candidate is ClientToServerMessageEnvelope {
  return serverToClientMessageEnvelopeSchema.safeParse(candidate).success;
}
